import {
  DEFAULT_PRINT_OPTIONS,
  printOptionsFromSettings,
  renderDocumentHtml,
  type PrintOptions,
  type RenderInput,
} from '@palaccount/documents';
import { useMemo } from 'react';

import { useEffectiveSettings } from '@/features/admin/api';

/**
 * The Print button, for documents.
 *
 * NFR-31 — "every document screen must have a Print action that produces a
 * clean page with no menus and no chrome." This is that action's engine: the
 * page printed is `renderDocumentHtml`'s output — a standalone document with
 * its own stylesheet — so no shell chrome can leak in by construction, which
 * is stronger than the prototype's hide-the-menu print style.
 *
 * ── Why an iframe and not `window.print()` ─────────────────────────────────
 * `window.print()` prints THE SCREEN with the shell hidden by `.no-print`
 * rules, which is a second design for the same document: it has the app's
 * fonts, the app's spacing, the app's collapsed panels, and none of the
 * `@page` sizing a printed invoice needs. It also means the paper a customer
 * receives and the PDF the worker renders come from two different templates,
 * and the moment either is edited they say different things.
 *
 * So Print renders `renderDocumentHtml(...)` — the SAME string the worker
 * hands to Chromium — into an offscreen iframe and prints that. One template,
 * two consumers, no drift, and `packages/documents` unit tests are assertions
 * about both.
 *
 * ── `srcdoc`, and why the sandbox is exactly these two tokens ──────────────
 * The document is built from tenant data. Every interpolation is escaped by
 * the `html` tagged template, and the sandbox is the belt to that pair of
 * braces: `sandbox="allow-modals allow-same-origin"`.
 *
 * WHAT IS ABSENT IS THE PROTECTION. There is no `allow-scripts`, so nothing
 * inside the frame can execute at all — not a `<script>`, not an `onerror=`,
 * not a `javascript:` URL. That, and not the origin, is what makes an
 * unescaped item description harmless here.
 *
 * WHY `allow-same-origin` IS NEEDED, and why it is safe WITHOUT
 * `allow-scripts`. A sandboxed frame with no `allow-same-origin` gets an
 * OPAQUE origin, which makes it cross-origin to its own parent — and `print`
 * is not on the short list of properties a cross-origin `Window` exposes, so
 * `frame.contentWindow.print()` throws a SecurityError and the Print button
 * silently does nothing. jsdom does not enforce cross-origin access, so a unit
 * test with a stubbed `contentWindow` cannot catch this; a real browser can.
 * Granting same-origin gives the frame the app's origin, which would matter if
 * code could run in it — and none can. The pair never to write is
 * `allow-scripts allow-same-origin` TOGETHER: a frame with both can reach up
 * and remove its own sandbox attribute.
 *
 * ── The cleanup ───────────────────────────────────────────────────────────
 * The frame is removed after `afterprint`, with a timer as the fallback:
 * Safari does not always fire `afterprint`, and a user who prints ten invoices
 * should not accumulate ten hidden frames. Removing it too eagerly cancels the
 * print in Chrome, which is why it is not removed synchronously.
 */

const CLEANUP_FALLBACK_MS = 60_000;

export function printDocumentHtml(html: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', 'print');
  // Off screen rather than `display: none` — a frame with no box does not
  // always lay out, and a frame that has not laid out prints blank.
  frame.style.cssText =
    'position:fixed;inset-inline-end:0;bottom:0;width:1px;height:1px;opacity:0;border:0;';
  frame.setAttribute('sandbox', 'allow-modals allow-same-origin');
  frame.srcdoc = html;

  let done = false;
  const remove = () => {
    if (done) return;
    done = true;
    frame.remove();
  };

  frame.addEventListener('load', () => {
    const view = frame.contentWindow;
    if (!view) {
      remove();
      return;
    }
    view.addEventListener('afterprint', remove);
    view.focus();
    view.print();
    // Safari's `afterprint` is unreliable; this is the sweep behind it.
    window.setTimeout(remove, CLEANUP_FALLBACK_MS);
  });

  document.body.append(frame);
}

// ── Print options (INV-H6 / UB-03) ──────────────────────────────────────────
//
// The eleven `print.*` company settings. They are applied INSIDE
// `renderDocumentHtml` (`packages/documents/src/print-options.ts`), as part of
// its input, so the worker's PDF and a browser print come out of one function
// with one set of options; this file's job is only to read the company's
// effective map off the API and hand it in. The first cut layered the options
// over the shared string here, in the browser alone — which is exactly the
// drift the paragraphs above exist to prevent, and the reason the worker's
// invoice kept its item-code column after the administrator switched it off.

export {
  DEFAULT_PRINT_OPTIONS,
  printOptionsFromSettings,
  resolvePrintLocale,
  type PrintLanguage,
  type PrintOptions,
  type PrintPaper,
} from '@palaccount/documents';

/**
 * The company's print options, read off `/api/settings/effective` (cached a
 * minute, like every other behavioural default a screen reads). Defaults
 * while loading, so a Print pressed in the first second prints today's paper
 * rather than waiting.
 */
export function usePrintOptions(): PrintOptions {
  const { data } = useEffectiveSettings();
  return useMemo(() => printOptionsFromSettings(data), [data]);
}

/**
 * Render the document WITH the company's print options — the string the Print
 * button hands the frame, and the string the Printing screen previews. Nothing
 * but `renderDocumentHtml` with `print` filled in: there is deliberately no
 * logic here for a test to pin that the worker could not also have.
 */
export function buildPrintHtml(input: RenderInput, options: PrintOptions = DEFAULT_PRINT_OPTIONS): string {
  return renderDocumentHtml({ ...input, print: options });
}

/** The Print button, with the company's options: build, then print. */
export function printDocument(input: RenderInput, options: PrintOptions = DEFAULT_PRINT_OPTIONS): void {
  printDocumentHtml(buildPrintHtml(input, options));
}
